import { Box, Popper } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { debounce } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { getPublisher } from '~/src/dash/api/console-api';
import { getPublishersNames } from '~/src/dash/api/publishers/publishers_methods';
import { IPublisherNames } from '~/src/dash/api/publishers/publishers_models';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { fontSizing, usePanel } from '~/src/dash/publicaUI';
import { SESSION_ACTIONS_SET } from '~/src/dash/reducers/session-reducers';
import { reload, RouteDashboards } from '~/src/dash/router/routes';
import { useMobileStore } from '~/src/dash/views/mobile/contexts';

const CustomPopper = (props: any) => {
  return <Popper {...props} style={{ width: 320 }} placement='bottom-end' />;
};

export const MobilePublisherSelect = () => {
  const { session, dispatch } = useMobileStore();
  const currentPublisherName = {
    id: Number(session.publisher.ID),
    name: session.publisher.Name,
  } as IPublisherNames;

  const isMounted = useRef(false);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<IPublisherNames | null>(currentPublisherName);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([] as IPublisherNames[]);

  const debouncedOnQuery = debounce(setInputValue, 1000);

  const { theme } = usePanel();

  useEffect(() => {
    if (isMounted.current && value && value.id && value !== currentPublisherName) {
      getPublisher({
        query: {
          publisher_id: value.id,
        },
      }).then((publisher) => {
        dispatch(
          SESSION_ACTIONS_SET({
            publisher,
          }),
        );
        reload(RouteDashboards, { publisherID: publisher.ID });
      });
    }
  }, [value]);

  useEffect(() => {
    if (isMounted.current && inputValue) {
      getPublishers(inputValue);
    }
    isMounted.current = true;
  }, [inputValue]);

  const onChange = (_e: any, newValue: IPublisherNames | null) => {
    if (newValue && newValue.id !== currentPublisherName.id) {
      unstable_batchedUpdates(() => {
        setOptions([newValue, ...options]);
        setValue(newValue);
      });
    }
  };

  const getPublishers = (query: string) => {
    if (query) {
      getPublishersNames(session.publisher.ID, query).then(
        (res: [IPublisherNames[] | undefined, string | undefined]) => {
          const models = Array.isArray(res[0]) ? res[0] : [];
          setOptions(models);
        },
      );
    }
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionLabel={(option) => option.name}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      selectOnFocus
      value={value}
      noOptionsText='Search Publisher by Name/ID'
      onChange={onChange}
      onInputChange={(_e, newInputValue) => {
        debouncedOnQuery(newInputValue);
      }}
      PopperComponent={CustomPopper}
      renderInput={(params) => (
        <div
          ref={params.InputProps.ref}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <input
            type='text'
            {...params.inputProps}
            style={{
              background: theme.menuBarBackground,
              border: 'none',
              borderRadius: '3px',
              fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
              fontSize: fontSizing.base,
              width: '128px',
              color: theme.menuBarText,
              padding: '2px 28px 2px 2px',
              fontWeight: FONT_WEIGHT.bold,
            }}
          />
          <div onClick={() => setOpen(true)}>
            <ExpandMore
              style={{
                position: 'absolute',
                bottom: '3px',
                right: '8px',
                fontSize: '16px',
                color: theme.menuBarText,
              }}
            />
          </div>
        </div>
      )}
      renderOption={(props) => {
        return (
          <Box component='li' {...props} id={props.id.toString()}>
            {`[${props.id}] ${props.name}`}
          </Box>
        );
      }}
    />
  );
};
